import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { defaultLanguage } from './hooks/useBrowserLanguage';
import { translations as ar } from './translations/ar';
import { translations as en } from './translations/en';
import { getLocale } from './utils/getLocale';

let currentCountry: string | undefined;

export const setI18nCountry = (country: string | undefined): void => {
  currentCountry = country;
};

const LANGUAGE_STORAGE_KEY = 'userBrowserLanguage';

const getInitialLanguage = (): string => {
  // Check localStorage first
  if (typeof window !== 'undefined') {
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (stored === 'ar' || stored === 'en') {
      return stored;
    }

    // Check browser language as fallback
    if (typeof navigator !== 'undefined' && navigator.language) {
      const browserLang = navigator.language.toLowerCase();
      if (browserLang.startsWith('ar')) {
        return 'ar';
      }
    }
  }
  return defaultLanguage;
};

i18n.use(initReactI18next).init({
  // Force synchronous init so tests don't render untranslated keys
  initImmediate: false,
  lng: getInitialLanguage(),
  fallbackLng: 'en',
  resources: {
    en: { translation: en },
    ar: { translation: ar },
  },
  defaultNS: 'translation',
  ns: ['translation'],
  interpolation: {
    escapeValue: false,
    prefix: '{',
    suffix: '}',
    formatSeparator: ',',
    format: (value, format, lng, options) => {
      if (format === 'number') {
        const numValue = typeof value === 'string' ? parseFloat(value) : value;
        if (isNaN(numValue)) {
          return String(value);
        }
        const locale = getLocale(lng ?? 'en', currentCountry);
        // Remove currency from options as we're only formatting the number, not currency
        const { currency, ...numberOptions } = options || {};
        return new Intl.NumberFormat(locale, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
          ...numberOptions,
        }).format(numValue);
      }
      if (format === 'currency') {
        const numValue = typeof value === 'string' ? parseFloat(value) : value;
        if (isNaN(numValue)) {
          return String(value);
        }

        const optionsWithFormatParams = options as {
          formatParams?: { amount?: { currency?: string; locale?: string } };
        };

        const currency = optionsWithFormatParams?.formatParams?.amount?.currency || 'AED';
        const locale =
          optionsWithFormatParams?.formatParams?.amount?.locale ||
          getLocale(lng ?? 'en', currentCountry);

        // Use standard Intl.NumberFormat for all locales
        return new Intl.NumberFormat(locale, {
          style: 'currency',
          currency,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(numValue);
      }
      return value;
    },
  },
});
// @ts-prune-ignore-next
export default i18n;
